import { useEffect } from "react";

// Ports all of the static landing page's vanilla-JS animation loops into a single
// ref-scoped effect. Logic is kept verbatim from the source; the only changes are:
//   - document.getElementById/querySelector(All) -> root.querySelector(All) (component-scoped)
//   - the window.__demoRestart global -> a local restartMap closure
//   - every timer/observer/listener is tracked and torn down on cleanup (StrictMode-safe)
export default function useDemoLoops(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const allTimers = [];
    const observers = [];
    const cleanups = [];
    const track = (id) => { allTimers.push(id); return id; };
    const restartMap = {};

    // ---- reveal-on-scroll ----
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("visible"); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    root.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    observers.push(io);

    // ---- call stream demo loop (hero scenes) ----
    (function () {
      const feed = root.querySelector("#cwFeed");
      if (!feed) return;
      const scenes = Array.from(feed.querySelectorAll(".scene"));
      const status = root.querySelector(".cwl .cw-status");
      const banner = root.querySelector("#capBanner");
      const capText = root.querySelector("#capText");
      const astate = root.querySelector("#cwAState");
      const atext = root.querySelector("#cwAText");
      let bannerT;
      function showBanner(text) {
        capText.textContent = text;
        banner.classList.add("show");
        clearTimeout(bannerT);
        bannerT = track(setTimeout(() => banner.classList.remove("show"), 2200));
      }
      function playScene(idx) {
        const scene = scenes[idx];
        const items = Array.from(scene.children);
        items.forEach((i) => i.classList.remove("on"));
        if (idx === 0 && status) status.textContent = "Listening…";
        if (scene.querySelector("#cwAction")) {
          astate.innerHTML = '<span class="aspin"></span>';
          atext.textContent = "Drafting the pricing doc…";
        }
        scenes.forEach((s) => s.classList.remove("cur"));
        scene.classList.add("cur");
        let t = 350;
        items.forEach((it) => {
          track(setTimeout(() => {
            it.classList.add("on");
            const cap = it.dataset.cap;
            if (cap) { showBanner(cap); if (status) status.textContent = "Assisting"; }
            if (it.id === "cwAction") {
              if (status) status.textContent = "Working…";
              track(setTimeout(() => {
                astate.innerHTML = '<span class="ok">&#10003;</span>';
                atext.textContent = "Pricing doc drafted. Ready to send";
                if (status) status.textContent = "Ready to send";
              }, 1500));
            }
          }, t));
          t += it.id === "cwAction" ? 2000 : 1050;
        });
        track(setTimeout(() => {
          scene.classList.remove("cur");
          track(setTimeout(() => playScene((idx + 1) % scenes.length), 380));
        }, t + 1400));
      }
      playScene(0);
    })();

    // helper: a tracked timer factory bound to a per-loop array (for restart)
    const makeT = (timers) => (fn, ms) => {
      const id = setTimeout(fn, ms);
      timers.push(id);
      allTimers.push(id);
      return id;
    };

    // ---- R1: contextual live assist loop ----
    (function () {
      const view = root.querySelector(".r1-view");
      if (!view) return;
      let timers = [];
      const T = makeT(timers);
      const status = root.querySelector("#r1Status");
      const banner = root.querySelector("#r1Banner");
      const bannerText = root.querySelector("#r1BannerText");
      const els = Array.from(view.children);
      function cap(t) { bannerText.textContent = t; banner.classList.add("show"); }
      function reset() {
        els.forEach((e) => e.classList.remove("on"));
        banner.classList.remove("show");
        status.textContent = "Listening…";
      }
      function play() {
        reset();
        T(() => { els[0].classList.add("on"); cap("Competitor question detected"); }, 500);
        T(() => { els[1].classList.add("on"); cap("Serving the team’s framing"); status.textContent = "Context served"; }, 1600);
        T(() => { els[2].classList.add("on"); }, 3300);
        T(() => { els[3].classList.add("on"); cap("Checking the pricing claim"); status.textContent = "Fact-checking"; }, 4800);
        T(() => { els[4].classList.add("on"); cap("Corrected from your own call"); status.textContent = "Answered, cited"; }, 6100);
        T(() => { view.classList.add("fade"); }, 9300);
        T(() => { reset(); view.classList.remove("fade"); }, 9800);
        T(() => { play(); }, 10400);
      }
      restartMap["demoR1"] = () => { timers.forEach(clearTimeout); timers = []; play(); };
    })();

    // ---- R2: agenda tracker loop ----
    (function () {
      const view = root.querySelector(".ag-view");
      if (!view) return;
      let timers = [];
      const T = makeT(timers);
      const status = root.querySelector("#agStatus");
      const banner = root.querySelector("#agBanner");
      const bannerText = root.querySelector("#agBannerText");
      const A = root.querySelector("#agbA");
      const B = root.querySelector("#agcB");
      const C = root.querySelector("#agcC");
      const subs = A.querySelectorAll(".ags");
      const notes = A.querySelectorAll(".ag-note");
      const foot = root.querySelector("#agFoot");
      function reset() {
        A.classList.remove("cov");
        A.querySelector(".agb-pill").textContent = "In progress";
        [B, C].forEach((x) => { x.classList.remove("cov", "nudge"); x.querySelector(".agb-pill").textContent = "Not covered"; });
        subs.forEach((x) => x.classList.remove("done"));
        notes.forEach((x) => x.classList.remove("on"));
        foot.classList.remove("on");
        banner.classList.remove("show");
        status.textContent = "Tracking";
      }
      function cover(i, t) {
        T(() => { subs[i].classList.add("done"); }, t);
        T(() => { notes[i].classList.add("on"); }, t + 420);
      }
      function play() {
        reset();
        cover(0, 1100);
        cover(1, 3000);
        cover(2, 4900);
        T(() => { A.classList.add("cov"); A.querySelector(".agb-pill").textContent = "Covered"; }, 6300);
        T(() => {
          B.classList.add("nudge");
          bannerText.textContent = "Pricing still pending · 10 min left";
          banner.classList.add("show");
          status.textContent = "Pending";
        }, 7300);
        T(() => {
          banner.classList.remove("show");
          B.classList.remove("nudge"); B.classList.add("cov");
          B.querySelector(".agb-pill").textContent = "Covered";
          status.textContent = "Tracking";
        }, 9500);
        T(() => { C.classList.add("cov"); C.querySelector(".agb-pill").textContent = "Covered"; }, 10900);
        T(() => { status.textContent = "Covered"; foot.classList.add("on"); }, 11800);
        T(() => { view.classList.add("fade"); }, 13600);
        T(() => { reset(); view.classList.remove("fade"); }, 14100);
        T(play, 14700);
      }
      restartMap["demoR2"] = () => { timers.forEach(clearTimeout); timers = []; play(); };
    })();

    // ---- M1: monday digest loop ----
    (function () {
      const view = root.querySelector(".m1-view");
      if (!view) return;
      let timers = [];
      const T = makeT(timers);
      const status = root.querySelector("#m1Status");
      const banner = root.querySelector("#m1Banner");
      const bannerText = root.querySelector("#m1BannerText");
      const quote = root.querySelector("#m1Quote");
      const btn = root.querySelector("#m1Btn");
      const els = Array.from(view.children);
      function cap(t) { bannerText.textContent = t; banner.classList.add("show"); }
      function reset() {
        els.forEach((e) => e.classList.remove("on"));
        quote.classList.remove("on");
        btn.textContent = "Approve";
        banner.classList.remove("show");
        status.textContent = "Reviewing";
      }
      function play() {
        reset();
        T(() => { els[0].classList.add("on"); cap("Reviewing the week"); }, 400);
        T(() => { els[1].classList.add("on"); cap("Flagging forecast risk"); status.textContent = "Flagging risk"; }, 1300);
        T(() => { quote.classList.add("on"); }, 2500);
        T(() => { els[2].classList.add("on"); cap("Pairing a fix from team memory"); status.textContent = "Coaching"; }, 3900);
        T(() => { els[3].classList.add("on"); cap("Checking pipeline hygiene"); }, 5100);
        T(() => { els[4].classList.add("on"); cap("One approval to act"); status.textContent = "Waiting on you"; }, 6300);
        T(() => { btn.textContent = "Approved ✓"; status.textContent = "Approved"; }, 8000);
        T(() => { els[5].classList.add("on"); cap("Drafts ready to review"); }, 8700);
        T(() => { view.classList.add("fade"); }, 11700);
        T(() => { reset(); view.classList.remove("fade"); }, 12200);
        T(() => { play(); }, 12800);
      }
      restartMap["demoM1"] = () => { timers.forEach(clearTimeout); timers = []; play(); };
    })();

    // ---- M2: deal risk loop ----
    (function () {
      const view = root.querySelector(".m2-view");
      if (!view) return;
      let timers = [];
      const T = makeT(timers);
      const status = root.querySelector("#m2Status");
      const banner = root.querySelector("#m2Banner");
      const bannerText = root.querySelector("#m2BannerText");
      const pill = root.querySelector("#m2RiskPill");
      const line = root.querySelector("#m2Line");
      const quote = root.querySelector("#m2Quote");
      const cite = root.querySelector("#m2Cite");
      const delta = root.querySelector("#m2Delta");
      const btn = root.querySelector("#m2Btn");
      const foot = root.querySelector("#m2Foot");
      const els = Array.from(view.children);
      function cap(t) { bannerText.textContent = t; banner.classList.add("show"); }
      function reset() {
        els.forEach((e) => e.classList.remove("on"));
        [pill, line, cite, delta].forEach((e) => e.classList.remove("on"));
        quote.classList.remove("on");
        foot.classList.remove("on");
        btn.textContent = "Draft it";
        banner.classList.remove("show");
        status.textContent = "Watching";
      }
      function play() {
        reset();
        T(() => { els[0].classList.add("on"); cap("Watching committed deals"); }, 400);
        T(() => { pill.classList.add("on"); line.classList.add("on"); cap("Flagging forecast risk"); status.textContent = "Risk flagged"; }, 1600);
        T(() => { quote.classList.add("on"); cite.classList.add("on"); cap("The quote behind the flag"); status.textContent = "Receipts"; }, 3000);
        T(() => { delta.classList.add("on"); }, 4400);
        T(() => { els[1].classList.add("on"); cap("Suggesting the fix"); status.textContent = "Suggesting"; }, 5800);
        T(() => { btn.textContent = "Drafted ✓"; status.textContent = "Waiting for review"; }, 7800);
        T(() => { foot.classList.add("on"); }, 8600);
        T(() => { view.classList.add("fade"); }, 11600);
        T(() => { reset(); view.classList.remove("fade"); }, 12100);
        T(() => { play(); }, 12700);
      }
      restartMap["demoM2"] = () => { timers.forEach(clearTimeout); timers = []; play(); };
    })();

    // ---- M3: ramp on team memory loop ----
    (function () {
      const view = root.querySelector(".m3-view");
      if (!view) return;
      let timers = [];
      const T = makeT(timers);
      const status = root.querySelector("#m3Status");
      const banner = root.querySelector("#m3Banner");
      const bannerText = root.querySelector("#m3BannerText");
      const foot = root.querySelector("#m3Foot");
      const els = Array.from(view.children);
      function cap(t) { bannerText.textContent = t; banner.classList.add("show"); }
      function reset() {
        els.forEach((e) => e.classList.remove("on"));
        foot.classList.remove("on");
        banner.classList.remove("show");
        status.textContent = "Listening";
      }
      function play() {
        reset();
        T(() => { els[0].classList.add("on"); cap("New hire, week 2"); }, 400);
        T(() => { cap("Searching team memory"); status.textContent = "Searching"; }, 1500);
        T(() => { els[1].classList.add("on"); cap("Answering from team memory"); status.textContent = "Answered, cited"; }, 2400);
        T(() => { els[2].classList.add("on"); }, 4600);
        T(() => { foot.classList.add("on"); cap("No senior rep interrupted"); }, 5800);
        T(() => { view.classList.add("fade"); }, 9400);
        T(() => { reset(); view.classList.remove("fade"); }, 9900);
        T(() => { play(); }, 10500);
      }
      restartMap["demoM3"] = () => { timers.forEach(clearTimeout); timers = []; play(); };
    })();

    // ---- C1: queryable intelligence loop ----
    (function () {
      const view = root.querySelector(".c1-view");
      if (!view) return;
      let timers = [];
      const T = makeT(timers);
      const status = root.querySelector("#c1Status");
      const banner = root.querySelector("#c1Banner");
      const bannerText = root.querySelector("#c1BannerText");
      const q = root.querySelector("#c1Q");
      const caret = root.querySelector("#c1Caret");
      const card = root.querySelector("#c1Card");
      const quote = root.querySelector("#c1Quote");
      const cite = root.querySelector("#c1Cite");
      const foot = root.querySelector("#c1Foot");
      const els = Array.from(view.children);
      const QTEXT = "Why did we lose deals last quarter?";
      function cap(t) { bannerText.textContent = t; banner.classList.add("show"); }
      function reset() {
        els.forEach((e) => e.classList.remove("on"));
        card.classList.remove("grow");
        quote.classList.remove("on"); cite.classList.remove("on"); foot.classList.remove("on");
        q.textContent = ""; caret.style.display = "inline-block";
        banner.classList.remove("show");
        status.textContent = "Ready";
      }
      function play() {
        reset();
        T(() => { els[0].classList.add("on"); cap("Ask anything"); }, 400);
        for (let i = 0; i < QTEXT.length; i++) {
          T(() => { q.textContent = QTEXT.slice(0, i + 1); }, 900 + i * 34);
        }
        T(() => { caret.style.display = "none"; els[1].classList.add("on"); cap("Reading every conversation"); status.textContent = "Reading"; }, 2400);
        T(() => { els[2].classList.add("on"); card.classList.add("grow"); cap("Ranked, with numbers"); status.textContent = "Answering"; }, 3800);
        T(() => { quote.classList.add("on"); cite.classList.add("on"); cap("The quote behind the number"); status.textContent = "Answered, cited"; }, 5600);
        T(() => { els[3].classList.add("on"); cap("Query anything"); }, 7200);
        T(() => { foot.classList.add("on"); }, 8400);
        T(() => { view.classList.add("fade"); }, 12000);
        T(() => { reset(); view.classList.remove("fade"); }, 12500);
        T(() => { play(); }, 13100);
      }
      restartMap["demoC1"] = () => { timers.forEach(clearTimeout); timers = []; play(); };
    })();

    // ---- C2: tribal knowledge capture loop ----
    (function () {
      const view = root.querySelector(".c2-view");
      if (!view) return;
      let timers = [];
      const T = makeT(timers);
      const status = root.querySelector("#c2Status");
      const banner = root.querySelector("#c2Banner");
      const bannerText = root.querySelector("#c2BannerText");
      const foot = root.querySelector("#c2Foot");
      const els = Array.from(view.children);
      function cap(t) { bannerText.textContent = t; banner.classList.add("show"); }
      function reset() {
        els.forEach((e) => e.classList.remove("on"));
        foot.classList.remove("on");
        banner.classList.remove("show");
        status.textContent = "Listening";
      }
      function play() {
        reset();
        T(() => { els[0].classList.add("on"); cap("Every call feeds the memory"); }, 400);
        T(() => { els[1].classList.add("on"); cap("A play, captured and linked"); status.textContent = "Capturing"; }, 1700);
        T(() => { els[2].classList.add("on"); cap("A different rep, a different call"); }, 3300);
        T(() => { els[3].classList.add("on"); cap("A requirement, captured and linked"); status.textContent = "Linking"; }, 4700);
        T(() => { els[4].classList.add("on"); }, 6300);
        T(() => { els[5].classList.add("on"); }, 7500);
        T(() => { els[6].classList.add("on"); cap("The knowledge base writes itself"); status.textContent = "Compounding"; }, 9000);
        T(() => { foot.classList.add("on"); }, 10400);
        T(() => { view.classList.add("fade"); }, 13800);
        T(() => { reset(); view.classList.remove("fade"); }, 14300);
        T(() => { play(); }, 14900);
      }
      restartMap["demoC2"] = () => { timers.forEach(clearTimeout); timers = []; play(); };
    })();

    // ---- R4: action items loop ----
    (function () {
      const view = root.querySelector(".r4-view");
      if (!view) return;
      let timers = [];
      const T = makeT(timers);
      const tasks = Array.from(view.querySelectorAll(".r3-task"));
      const insight = root.querySelector("#r4Insight");
      const status = root.querySelector("#r4Status");
      const banner = root.querySelector("#r4Banner");
      const bt = root.querySelector("#r4BannerText");
      const btn = root.querySelector("#r4Btn");
      const foot = root.querySelector("#r4Foot");
      let bT;
      function flash(text, hold) {
        bt.textContent = text;
        banner.classList.add("show");
        clearTimeout(bT);
        bT = track(setTimeout(() => banner.classList.remove("show"), hold || 2300));
      }
      function st(i, html, label) {
        tasks[i].querySelector(".r3-st").innerHTML = html;
        tasks[i].querySelector(".r3-stt").textContent = label;
      }
      function reset() {
        tasks.forEach((t, i) => { t.classList.remove("on"); st(i, '<span class="dot-q">&#9719;</span>', "Captured"); });
        insight.classList.remove("on");
        btn.classList.remove("sent");
        btn.textContent = "Draft it";
        foot.classList.remove("on");
        banner.classList.remove("show");
        status.textContent = "Capturing";
      }
      function play() {
        reset();
        T(() => { flash("Capturing action items"); }, 500);
        tasks.forEach((t, i) => T(() => t.classList.add("on"), 800 + i * 900));
        T(() => { flash("Auto-tracking via your connections"); status.textContent = "Tracking"; }, 3700);
        T(() => st(0, '<span class="okg">&#10003;</span>', "Scheduled · Thu 11:00"), 4400);
        T(() => st(1, '<span class="okg">&#10003;</span>', "Sent · 2:40 PM"), 5500);
        T(() => st(2, '<span class="amber"></span>', "Blocked · no pricing doc found"), 6600);
        T(() => { insight.classList.add("on"); flash("Learning across your calls", 2800); status.textContent = "Suggesting"; }, 7800);
        T(() => {
          btn.classList.add("sent"); btn.textContent = "Drafting ✓";
          st(2, '<span class="okg">&#10003;</span>', "Draft ready · review to send");
          status.textContent = "Unblocked";
          foot.classList.add("on");
        }, 10200);
        T(() => { view.classList.add("fade"); }, 12600);
        T(() => { reset(); view.classList.remove("fade"); }, 13100);
        T(play, 13700);
      }
      restartMap["demoR4"] = () => { timers.forEach(clearTimeout); timers = []; play(); };
    })();

    // ---- demo player: auto-touring with visible progress ----
    (function () {
      const player = root.querySelector("#player");
      if (!player) return;
      const rows = Array.from(player.querySelectorAll(".prow"));
      const stages = Array.from(player.querySelectorAll(".play-stage>.stage"));
      const tabs = Array.from(root.querySelectorAll("#playerTabs button"));
      let cur = -1, auto = true, advT = null, started = false, visible = false;

      function setGroup(g) {
        rows.forEach((r) => r.classList.toggle("g-on", r.dataset.g === g));
        tabs.forEach((t) => t.classList.toggle("active", t.dataset.g === g));
      }
      function fillBar(row, dur) {
        const bar = row.querySelector(".pbar i");
        bar.style.transition = "none";
        bar.style.width = "0%";
        void bar.offsetWidth;
        bar.style.transition = "width " + dur + "ms linear";
        bar.style.width = "100%";
      }
      function showStep(i) {
        clearTimeout(advT);
        cur = i;
        const row = rows[i];
        setGroup(row.dataset.g);
        rows.forEach((r, j) => r.classList.toggle("act", j === i));
        stages.forEach((st) => st.classList.toggle("curD", st.dataset.w === row.dataset.demo));
        const k = row.dataset.demo, dur = +row.dataset.dur;
        if (restartMap[k]) restartMap[k]();
        fillBar(row, dur);
        if (auto) advT = track(setTimeout(() => { if (visible) showStep((cur + 1) % rows.length); }, dur));
      }
      rows.forEach((r, i) => {
        const handler = () => { auto = false; showStep(i); };
        r.addEventListener("click", handler);
        cleanups.push(() => r.removeEventListener("click", handler));
      });
      tabs.forEach((t) => {
        const handler = () => {
          const first = rows.findIndex((r) => r.dataset.g === t.dataset.g);
          showStep(first);
        };
        t.addEventListener("click", handler);
        cleanups.push(() => t.removeEventListener("click", handler));
      });
      const io2 = new IntersectionObserver((es) => {
        es.forEach((en) => {
          visible = en.isIntersecting;
          if (visible && !started) { started = true; showStep(0); }
          else if (visible && started && auto && cur > -1) { showStep(cur); }
          else if (!visible) { clearTimeout(advT); }
        });
      }, { threshold: 0.25 });
      io2.observe(player);
      observers.push(io2);
    })();

    return () => {
      allTimers.forEach(clearTimeout);
      observers.forEach((o) => o.disconnect());
      cleanups.forEach((fn) => fn());
    };
  }, [rootRef]);
}
